import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { Avatar, Box } from '@material-ui/core';
import { CustomTheme } from 'common/ui/interfaces';
import { getInitials } from 'helpers/common';
import cx from 'classnames';
import profilePlaceholder from 'img/photo-placeholder.png';
import SearchUsersAutocompleteSelect from '../../../SearchUsersAutocomplete/SearchUsersAutocomplete';
import { IInvoiceDetailsImt } from '../../../../interfaces/invoices';
import * as actions from '../../../../state/invoice/actions';
import messages from 'common/messages/messages';
import inputLabels from '../../../../messages/inputLabels';
import searchInputMessages from '../../../SearchUsersAutocomplete/messages';
import { PeakModules } from 'common/constants/peakModules';
import { useLazyGetSalespersons } from 'api/dictionary.api';
import { IEmployeeBasicInfo } from 'interfaces/employee.interfaces';

interface IInvoiceSalespersonSelector {
  selectedInvoice?: IInvoiceDetailsImt;
  module: PeakModules;
  isPaymentStep?: boolean;
  disabled: boolean;
}

const useStyles = makeStyles((theme: CustomTheme) => ({
  optionAvatar: {
    height: 24,
    width: 24,
    marginRight: theme.spacing(1),
  },
  avatarWrap: {
    position: 'relative',
    flex: 'none',
    borderRadius: '50%',
    backgroundColor: theme.palette.secondary.light,
  },
  avatarInitial: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
    fontSize: '12px',
    fontWeight: 700,
    color: theme.palette.darkBackground?.light,
  },
  text: {
    flexGrow: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    alignSelf: 'center',
    fontSize: theme.typography.h5.fontSize,
  },
}));

const InvoiceSalespersonSelector: FC<IInvoiceSalespersonSelector> = props => {
  const { selectedInvoice, module, isPaymentStep = false, disabled } = props;
  const classes = useStyles(props);
  const dispatch = useDispatch();
  const intl = useIntl();
  const hasSalesperson = !!selectedInvoice?.get('salesperson');

  const [getSalespersons] = useLazyGetSalespersons();

  const handleSelectSalesperson = (selectedSales): void => {
    dispatch(
      actions.setSalespersonToInvoice(
        selectedInvoice?.get('id') || '',
        selectedSales.id,
        isPaymentStep,
        module,
      ),
    );
  };

  const renderOption = (option: IEmployeeBasicInfo): JSX.Element => {
    const { firstName, lastName, employeeIdentifier, imageUrl } = option;
    const initials = getInitials(firstName, lastName);

    return (
      <>
        <Box mr={12} className={cx(classes.avatarWrap, classes.optionAvatar)}>
          {initials && !imageUrl ? (
            <Box className={classes.avatarInitial}>{initials}</Box>
          ) : (
            <Avatar
              src={imageUrl || profilePlaceholder}
              alt="avatar-icon"
              className={classes.optionAvatar}
            />
          )}
        </Box>
        <Box className={classes.text}>{`${employeeIdentifier} - ${firstName} ${lastName}`}</Box>
      </>
    );
  };

  return (
    <SearchUsersAutocompleteSelect
      renderOption={renderOption}
      selectedValue={selectedInvoice?.get('salesperson')?.toJS()}
      onChange={handleSelectSalesperson}
      getOptionsByValue={getSalespersons}
      hasStartSearchMode
      hideNOS
      label={intl.formatMessage(inputLabels.salesperson)}
      searchPlaceholder={intl.formatMessage(searchInputMessages.searchSalesperson)}
      error={!hasSalesperson}
      helperText={
        !hasSalesperson ? intl.formatMessage(messages.salespersonRequiredWarning) : undefined
      }
      disabled={disabled}
    />
  );
};

export default InvoiceSalespersonSelector;
